import service from '@services/index';

const SAM_BASE_URL = 'https://sam-dev.avawatz.com';
const SAM_3D_BASE_URL = 'https://sam-3d-dev.avawatz.com';

export interface SamStatusResponse {
  message: string;
  percentage: number;
}

export interface SamSegmentResponse {
  segments: number[][];
}

/** Raw perform-sam payload (segments and/or mask_0). */
export interface SamPerformRawResponse {
  segments?: number[][];
  /** Flat [x1, y1, x2, y2, ...] or legacy [[x, y], ...] contour. */
  mask_0?: number[] | number[][];
}

export const normalizeSamSegments = (
  data: SamPerformRawResponse
): number[][] => {
  if (data.segments?.length) return data.segments;

  const mask = data.mask_0;
  if (!mask?.length) return [];

  if (typeof mask[0] === 'number') {
    return [mask as number[]];
  }

  const pairs = mask as number[][];
  return [pairs.flatMap(([x, y]) => [x, y])];
};

export type SamStatusResult =
  | { status: 200; data: SamStatusResponse }
  | { status: 400; data: null };

export const checkSamLabellingStatus = async (
  projectId: string | number
): Promise<SamStatusResult> => {
  try {
    const response = await service.get(
      `/check-labelling-status/${projectId}`,
      null,
      null,
      SAM_BASE_URL
    );
    return {
      status: 200,
      data: response.data as SamStatusResponse,
    };
  } catch (err: unknown) {
    const error = err as { status?: number };
    if (error?.status === 400) {
      return { status: 400, data: null };
    }
    throw err;
  }
};

export const initiateSam = async (projectId: string | number) => {
  const response = await service.post(
    '/initiate-sam',
    { project_id: projectId },
    SAM_BASE_URL
  );
  return response;
};

export const performSam = async (
  projectId: string | number,
  imageId: string,
  x: number,
  y: number
): Promise<SamSegmentResponse> => {
  const response = await service.get(
    `/perform-sam/${projectId}/${imageId}`,
    { x, y },
    null,
    SAM_BASE_URL
  );
  const data = response.data as SamPerformRawResponse;
  return { segments: normalizeSamSegments(data) };
};

/** Extract only mask_0 from 3D NIFTI perform-sam response. */
export const normalizeSamNiftiMask = (
  data: SamPerformRawResponse
): number[][] => {
  const mask = data.mask_0;
  if (!mask?.length) return [];

  if (typeof mask[0] === 'number') {
    return [mask as number[]];
  }

  const pairs = mask as number[][];
  return [pairs.flatMap(([x, y]) => [x, y])];
};

export const checkSamNiftiStatus = async (
  projectId: string | number,
  volumeId: string
): Promise<SamStatusResult> => {
  try {
    const response = await service.get(
      `/check-nifti-status/${projectId}/${volumeId}`,
      null,
      null,
      SAM_3D_BASE_URL
    );
    return {
      status: 200,
      data: response.data as SamStatusResponse,
    };
  } catch (err: unknown) {
    const error = err as { status?: number };
    if (error?.status === 400) {
      return { status: 400, data: null };
    }
    throw err;
  }
};

export const initiateSamNifti = async (
  projectId: string | number,
  volumeId: string
) => {
  const response = await service.post(
    '/initiate-sam-nifti',
    { project_id: projectId, volume_id: volumeId },
    SAM_3D_BASE_URL
  );
  return response;
};

export const performSamNifti = async (
  projectId: string | number,
  volumeId: string,
  sliceIdx: number,
  x: number,
  y: number
): Promise<SamSegmentResponse> => {
  const response = await service.get(
    `/perform-sam-nifti/${projectId}/${volumeId}`,
    { slice_idx: sliceIdx, x, y },
    null,
    SAM_3D_BASE_URL
  );
  const data = response.data as SamPerformRawResponse;
  return { segments: normalizeSamNiftiMask(data) };
};
